import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { inr } from "@/lib/format";
import { cn } from "@/lib/utils";

const priceOf = (p) => p.price ?? p.sale_price ?? p.mrp;
const isFinished = (p) => String(p.kind || p.product_type || "").toLowerCase() === "finished" || p.is_finished;

// Group the catalog into product families (all variants of one product) and single products.
export function useCatalogGroups(products = []) {
  return useMemo(() => {
    const live = products.filter((p) => p.status !== "inactive");
    const fams = new Map();
    const singles = [];
    live.forEach((p) => {
      if (p.variant_group_id) {
        const f = fams.get(p.variant_group_id) || { id: p.variant_group_id, name: p.variant_base_name || p.name, category: p.category, kind: isFinished(p) ? "finished" : "raw", items: [] };
        f.items.push(p);
        fams.set(p.variant_group_id, f);
      } else singles.push(p);
    });
    const families = [...fams.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const byId = Object.fromEntries(live.map((p) => [p.id, p]));
    const famById = Object.fromEntries(families.map((f) => [f.id, f]));
    return { families, singles: singles.sort((a, b) => String(a.name).localeCompare(String(b.name))), byId, famById };
  }, [products]);
}

// Pick single products and/or whole families (every variant). value = { product_ids: [], product_families: [] }
export function ProductPicker({ open, onClose, products, value, onChange, title = "Pick products" }) {
  const { families, singles } = useCatalogGroups(products);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(value);
  const [openFam, setOpenFam] = useState({});
  const match = (p) => !q || `${p.name} ${p.category || ""} ${p.sku || ""}`.toLowerCase().includes(q.toLowerCase());
  const toggle = (key, id) => setSel((s) => ({ ...s, [key]: s[key].includes(id) ? s[key].filter((x) => x !== id) : [...s[key], id] }));
  const famList = families.filter((f) => match(f) || f.items.some(match));
  const singleList = singles.filter(match);
  const count = sel.product_ids.length + sel.product_families.length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) setSel(value); else onClose(); }}>
      <DialogContent className="sm:max-w-xl" data-testid="product-picker" onOpenAutoFocus={() => setSel(value)}>
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>Tick a product with variants to include every colour / size, or open it to pick single variants.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Icons.Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, category, SKU…" className="pl-8" data-testid="picker-search" autoFocus />
        </div>
        <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-border divide-y divide-border">
          {famList.map((f) => {
            const on = sel.product_families.includes(f.id);
            const some = f.items.filter((p) => sel.product_ids.includes(p.id)).length;
            return (
              <div key={f.id}>
                <div className="flex items-center gap-2.5 px-3 py-2">
                  <Checkbox checked={on} onCheckedChange={() => toggle("product_families", f.id)} data-testid={`pick-family-${f.id}`} />
                  <button type="button" className="flex-1 text-left" onClick={() => setOpenFam((s) => ({ ...s, [f.id]: !s[f.id] }))}>
                    <span className="text-sm font-medium">{f.name}</span>
                    <span className="text-[11px] text-muted-foreground ml-2">{f.items.length} variants · {f.category || "—"}{some && !on ? ` · ${some} picked` : ""}</span>
                  </button>
                  <Icons.ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", openFam[f.id] && "rotate-180")} />
                </div>
                {openFam[f.id] && (
                  <div className="pl-9 pb-2 space-y-1">
                    {f.items.map((p) => (
                      <label key={p.id} className={cn("flex items-center gap-2 text-xs", on && "opacity-50")}>
                        <Checkbox disabled={on} checked={on || sel.product_ids.includes(p.id)} onCheckedChange={() => toggle("product_ids", p.id)} />
                        {p.variant_label || p.name}<span className="text-muted-foreground ml-auto pr-3 font-mono">{priceOf(p) != null ? inr(priceOf(p)) : ""}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {singleList.map((p) => (
            <label key={p.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-accent/40">
              <Checkbox checked={sel.product_ids.includes(p.id)} onCheckedChange={() => toggle("product_ids", p.id)} data-testid={`pick-product-${p.id}`} />
              <span className="text-sm flex-1">{p.name}<span className="text-[11px] text-muted-foreground ml-2">{p.category || "—"}{isFinished(p) ? " · finished" : ""}</span></span>
              <span className="text-xs font-mono text-muted-foreground">{priceOf(p) != null ? inr(priceOf(p)) : ""}</span>
            </label>
          ))}
          {!famList.length && !singleList.length && <p className="p-6 text-center text-sm text-muted-foreground">No products match.</p>}
        </div>
        <DialogFooter className="items-center">
          <span className="text-xs text-muted-foreground mr-auto">{count} picked</span>
          <Button variant="ghost" onClick={() => setSel({ product_ids: [], product_families: [] })}>Clear</Button>
          <Button onClick={() => { onChange(sel); onClose(); }} data-testid="picker-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
